/**
 * Settings Profile — Real Better Auth session and profile management.
 */
import { useState, useEffect, useCallback, type FormEvent } from "react";
import { useAuth } from "@/lib/auth-provider";
import { authClient } from "@/lib/auth-client";
import { User, Shield, Monitor, Trash2, Loader2 } from "lucide-react";

const TIMEZONES: readonly string[] = (() => {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return [
      "UTC", "Europe/London", "Europe/Berlin", "Europe/Paris",
      "Europe/Bucharest", "America/New_York", "America/Chicago",
      "America/Denver", "America/Los_Angeles", "Asia/Tokyo",
      "Asia/Shanghai", "Asia/Kolkata", "Australia/Sydney",
    ];
  }
})();

interface SessionRecord {
  id: string;
  createdAt: string;
  updatedAt: string;
  userAgent?: string;
  ipAddress?: string;
}

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name ?? "");
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [browserNotifications, setBrowserNotifications] = useState(false);
  const [weeklyDigest, setWeeklyDigest] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Sessions
  const [sessions, setSessions] = useState<SessionRecord[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionsError, setSessionsError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    setSessionsError(null);
    try {
      const result = await authClient.listSessions();
      if ("error" in result && result.error) {
        setSessionsError(String((result.error as Record<string, unknown>).message ?? "Failed to load sessions"));
      } else if ("data" in result && Array.isArray(result.data)) {
        setSessions(result.data.map((s: Record<string, unknown>) => ({
          id: String(s.id ?? ""),
          createdAt: String(s.createdAt ?? ""),
          updatedAt: String(s.updatedAt ?? ""),
          userAgent: s.userAgent ? String(s.userAgent) : undefined,
          ipAddress: s.ipAddress ? String(s.ipAddress) : undefined,
        })));
      }
    } catch {
      setSessionsError("Session listing is not available");
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSessions();
  }, [loadSessions]);

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await authClient.updateUser({ name });
      if ("error" in result && result.error) {
        setError(String((result.error as Record<string, unknown>).message ?? "Failed to update profile"));
      } else {
        setSuccess("Profile updated");
        await refresh();
      }
    } catch {
      setError("Profile update failed");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevokeSession = async (sessionId: string) => {
    setRevokingId(sessionId);
    try {
      await authClient.revokeSession({ token: sessionId });
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
    } catch {
      setSessionsError("Failed to revoke session");
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div className="max-w-lg space-y-8">
      <section>
        <div className="flex items-center gap-2 mb-4">
          <User className="h-5 w-5 text-[var(--color-primary)]" />
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Profile</h2>
        </div>

        {error && (
          <div className="mb-3 rounded-lg border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 px-4 py-2 text-sm text-[var(--color-error)]">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-3 rounded-lg border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 px-4 py-2 text-sm text-[var(--color-success)]">
            {success}
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-[var(--color-foreground)]">Full name</label>
            <input
              id="name"
              type="text"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30"
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-[var(--color-foreground)]">Email</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={user?.email ?? ""}
              disabled
              className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-muted-foreground)]"
            />
            <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">Contact support to change your email</p>
          </div>
          <div>
            <label htmlFor="timezone" className="block text-sm font-medium text-[var(--color-foreground)]">Timezone</label>
            <select
              id="timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
            >
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>
              ))}
            </select>
          </div>

          <div>
            <p className="text-sm font-medium text-[var(--color-foreground)] mb-2">Notifications</p>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-[var(--color-foreground)]">
                <input type="checkbox" checked={emailNotifications} onChange={(e) => setEmailNotifications(e.target.checked)} className="rounded" />
                Email notifications
              </label>
              <label className="flex items-center gap-2 text-sm text-[var(--color-foreground)]">
                <input type="checkbox" checked={browserNotifications} onChange={(e) => setBrowserNotifications(e.target.checked)} className="rounded" />
                Browser push notifications
              </label>
              <label className="flex items-center gap-2 text-sm text-[var(--color-foreground)]">
                <input type="checkbox" checked={weeklyDigest} onChange={(e) => setWeeklyDigest(e.target.checked)} className="rounded" />
                Weekly activity digest
              </label>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSaving}
            className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {isSaving ? "Saving..." : "Save changes"}
          </button>
        </form>
      </section>

      <hr className="border-[var(--color-border)]" />

      <section>
        <div className="flex items-center gap-2 mb-4">
          <Shield className="h-5 w-5 text-[var(--color-muted-foreground)]" />
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Security</h2>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between rounded-lg border border-[var(--color-border)] p-4">
            <div>
              <p className="text-sm font-medium text-[var(--color-foreground)]">Password</p>
              <p className="text-xs text-[var(--color-muted-foreground)]">Change your password</p>
            </div>
            <a href="/auth/change-password" className="rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-surface)]">
              Change
            </a>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-[var(--color-border)] p-4">
            <div>
              <p className="text-sm font-medium text-[var(--color-foreground)]">Two-factor authentication</p>
              <p className="text-xs text-[var(--color-muted-foreground)]">Add an extra layer of security</p>
            </div>
            <a href="/auth/two-factor" className="rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-surface)]">
              Enable
            </a>
          </div>
        </div>
      </section>

      <hr className="border-[var(--color-border)]" />

      <section>
        <div className="flex items-center gap-2 mb-4">
          <Monitor className="h-5 w-5 text-[var(--color-muted-foreground)]" />
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Active Sessions</h2>
        </div>

        {sessionsError && (
          <p className="text-sm text-[var(--color-muted-foreground)] rounded-lg border border-[var(--color-border)] p-4">
            {sessionsError}
          </p>
        )}

        {sessionsLoading && !sessionsError && (
          <div className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)] p-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading sessions...
          </div>
        )}

        {!sessionsLoading && !sessionsError && sessions.length === 0 && (
          <p className="text-sm text-[var(--color-muted-foreground)] rounded-lg border border-[var(--color-border)] p-4">
            No active sessions found.
          </p>
        )}

        {!sessionsLoading && !sessionsError && sessions.length > 0 && (
          <div className="space-y-2">
            {sessions.map((session) => (
              <div key={session.id} className="flex items-center justify-between rounded-lg border border-[var(--color-border)] p-3">
                <div>
                  <p className="text-sm text-[var(--color-foreground)]">
                    {session.userAgent ? session.userAgent.slice(0, 60) : "Unknown device"}
                  </p>
                  <p className="text-xs text-[var(--color-muted-foreground)]">
                    Created {new Date(session.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() => void handleRevokeSession(session.id)}
                  disabled={revokingId === session.id}
                  className="rounded-lg border border-[var(--color-border)] p-1.5 text-[var(--color-muted-foreground)] hover:text-[var(--color-error)] hover:border-[var(--color-error)]/30 disabled:opacity-50"
                  title="Revoke session"
                >
                  {revokingId === session.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
